import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { ApiService } from './api.service';
import { buildApiUrl } from '../config/api.constants';
import { DOCUMENT } from '@angular/common';

export interface MatchAccessTokenValidate {
  match_id: number;
  valid: boolean;
  expires_at: string;
}

export interface GeneratedAccessToken {
  id: number;
  token: string;
  match_id: number;
  label: string | null;
  created_by: number | null;
  created_at: string;
  expires_at: string;
  is_revoked: boolean;
}

@Injectable({ providedIn: 'root' })
export class MatchAccessTokenService {
  private readonly apiService = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);

  // Held in an in-memory signal for the active session. Also mirrored into
  // sessionStorage (per match_id) so a page reload can re-validate without the
  // user re-opening the original share URL. sessionStorage is tab-scoped and
  // cleared when the tab closes, which bounds the XSS exposure window.
  readonly currentMatchAccessToken = signal<string | null>(null);

  // Which match the in-memory token authorises. A token is only ever valid for
  // its own match, so every consumer must check the id before using it — sending
  // match A's token on a request for match B makes the backend take the token
  // path and reject it (403), even when the caller's own session would have
  // been accepted (FAS-9ym8).
  readonly currentMatchAccessTokenMatchId = signal<number | null>(null);

  private static readonly STORAGE_PREFIX = 'match_access_token:';

  private get sessionStorage(): Storage | null {
    return this.document.defaultView?.sessionStorage ?? null;
  }

  private storageKey(matchId: number): string {
    return `${MatchAccessTokenService.STORAGE_PREFIX}${matchId}`;
  }

  /**
   * The validated token authorising *this* match, or null. Prefers the
   * in-memory signal and falls back to sessionStorage, which is what makes a
   * reload work when the share URL has already been stripped.
   */
  getTokenForMatch(matchId: number): string | null {
    if (!Number.isFinite(matchId)) return null;
    if (this.currentMatchAccessTokenMatchId() === matchId) {
      const token = this.currentMatchAccessToken();
      if (token) return token;
    }
    return this.getStoredToken(matchId);
  }

  /** Read a previously validated token for a match (e.g. after page reload). */
  getStoredToken(matchId: number): string | null {
    if (!Number.isFinite(matchId)) return null;
    try {
      return this.sessionStorage?.getItem(this.storageKey(matchId)) ?? null;
    } catch {
      return null;
    }
  }

  /** Remove a stored token (e.g. when it failed re-validation). */
  clearStoredToken(matchId: number): void {
    if (this.currentMatchAccessTokenMatchId() === matchId) {
      this.currentMatchAccessToken.set(null);
      this.currentMatchAccessTokenMatchId.set(null);
    }
    try {
      this.sessionStorage?.removeItem(this.storageKey(matchId));
    } catch {
      // sessionStorage unavailable — nothing to clear.
    }
  }

  /**
   * Validate a raw token string via the public endpoint.
   * On success, sets currentMatchAccessToken, persists it in sessionStorage
   * keyed by match_id, and returns the match_id.
   */
  validateAndStore(tokenStr: string): Observable<number | null> {
    // Send token via X-Match-Access-Token header — keeps it out of URL/referer/access logs.
    // Bypasses ApiService wrapper because the auth interceptor only attaches the header from
    // the signal, which is only set AFTER successful validation here.
    const url = buildApiUrl('/api/access-token/validate');
    const headers = new HttpHeaders({ 'X-Match-Access-Token': tokenStr });
    return this.http.get<MatchAccessTokenValidate>(url, { headers }).pipe(
      map((response) => {
        if (!response.valid) {
          return null;
        }
        this.currentMatchAccessToken.set(tokenStr);
        this.currentMatchAccessTokenMatchId.set(response.match_id);
        try {
          this.sessionStorage?.setItem(this.storageKey(response.match_id), tokenStr);
        } catch {
          // sessionStorage unavailable (private mode / quota) — degrade to
          // in-memory only; reload will require the original share URL.
        }
        return response.match_id;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Generate a new 24-hour access token for a match.
   * Requires the caller to be authenticated (JWT attached by interceptor).
   */
  generateToken(matchId: number, label?: string): Observable<GeneratedAccessToken> {
    const url = buildApiUrl(`/api/matches/${matchId}/access-tokens/`);
    return this.apiService.customPost<GeneratedAccessToken>(url, { match_id: matchId, label: label ?? null });
  }

  /**
   * Build all three shareable URLs (admin, graphics, events) for a token.
   */
  buildShareUrls(matchId: number, token: string): ShareUrls {
    const origin = this.document.location?.origin ?? '';
    const qs = `?access_token=${token}`;
    return {
      admin:    `${origin}/scoreboard/match/${matchId}/admin${qs}`,
      graphics: `${origin}/scoreboard/match/${matchId}/graphics${qs}`,
      events:   `${origin}/scoreboard/match/${matchId}/events${qs}`,
      time:     `${origin}/scoreboard/match/${matchId}/time${qs}`,
    };
  }
}

export interface ShareUrls {
  admin: string;
  graphics: string;
  events: string;
  time: string;
}
